/**
 * Upload API adapter — tinypresent.com (create-image-2d) or mock for local dev.
 */
(function () {
  "use strict";

  function getConfig() {
    return (
      window.AI_UPLOAD_CONFIG || {
        mode: "mock",
        jobNum: 1,
        mockImageUrls: [],
      }
    );
  }

  function sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  function getApiBaseUrl() {
    return (getConfig().apiBaseUrl || "").replace(/\/$/, "");
  }

  function getApiHeaders() {
    const headers = {};
    const token = getConfig().apiToken || "";
    if (token) {
      headers["X-Api-Token"] = token;
    }
    return headers;
  }

  function getCustomerId() {
    const key = getConfig().customerIdStorageKey || "mogcustom_customer_id";
    let id = localStorage.getItem(key);
    if (!id) {
      id =
        "guest-" +
        (typeof crypto !== "undefined" && crypto.randomUUID
          ? crypto.randomUUID()
          : Date.now().toString(36) + Math.random().toString(36).slice(2));
      localStorage.setItem(key, id);
    }
    return id;
  }

  function getProductGenerationConfig(uploader) {
    const section =
      uploader?.trigger?.closest?.(".ai-upload-section") ||
      document.querySelector(".ai-upload-section");
    const script = section?.querySelector(".product-generation-config");
    if (!script) return {};
    try {
      return JSON.parse(script.textContent) || {};
    } catch {
      return {};
    }
  }

  function getMockUrls(count) {
    const cfg = getConfig();
    const urls = [...(cfg.mockImageUrls || [])];
    while (urls.length < count) {
      const n = urls.length + 1;
      urls.push(
        `https://placehold.co/512x512/e8e8e8/333333?text=AI+Design+${n}`
      );
    }
    return urls.slice(0, count);
  }

  async function parseJsonResponse(response) {
    try {
      return await response.json();
    } catch {
      return {};
    }
  }

  function extractUserImageUrls(data) {
    if (!data || typeof data !== "object") return [];

    const fromArray =
      data.userImageUrls ||
      data.inputImageUrls ||
      data.sourceImageUrls ||
      data.uploadedUserImages;
    if (Array.isArray(fromArray)) {
      return fromArray.filter(Boolean);
    }

    const urls = [];
    if (data.userImageUrl) urls.push(data.userImageUrl);
    if (data.userImageUrl2) urls.push(data.userImageUrl2);
    if (urls.length) return urls;

    if (data.sourceImageUrl) urls.push(data.sourceImageUrl);
    if (data.sourceImageUrl2) urls.push(data.sourceImageUrl2);
    return urls;
  }

  const DAILY_LIMIT_MESSAGE =
    "You've reached today's limit of 10 AI previews. Please try again tomorrow.";

  function throwApiError(response, data, fallback) {
    const detail = data.detail ? String(data.detail) : "";
    const msg =
      data.message ||
      detail ||
      data.error ||
      data.name ||
      (Array.isArray(data.errors) ? data.errors.join(", ") : "") ||
      fallback ||
      `Request failed (${response.status})`;

    if (detail && detail !== msg) {
      console.error("[AiUpload] error detail:", detail);
    }

    if (response.status === 403) {
      const err = new Error(DAILY_LIMIT_MESSAGE);
      err.isDailyLimit = true;
      throw err;
    }

    if (response.status === 429) {
      const err = new Error("Please wait a moment and try again.");
      err.isRateLimit = true;
      throw err;
    }

    throw new Error(msg);
  }

  class MockUploadAdapter {
    constructor() {
      this.userImageUrls = [];
    }

    async upload(file, options = {}) {
      await sleep(400);
      const urls = ["https://placehold.co/512x512/f5f5f5/666?text=User+1"];
      if (options.secondFile) {
        urls.push("https://placehold.co/512x512/f5f5f5/666?text=User+2");
      }
      this.userImageUrls = urls;
      return "mock-task-id";
    }

    async poll(uploader) {
      const jobNum = getConfig().jobNum || 1;
      const urls = getMockUrls(jobNum);
      let initialized = false;

      for (let index = 0; index < urls.length; index++) {
        if (window.aiUploadModal?.abortController?.signal.aborted) {
          throw new DOMException("Upload cancelled", "AbortError");
        }
        await sleep(600);
        if (!initialized) {
          window.aiUploadModal.initImageSelection();
          initialized = true;
        }
        window.aiUploadModal.updateImageSlot(index, urls[index]);
        if (typeof uploader?.config?.onImageGenerated === "function") {
          uploader.config.onImageGenerated(urls[index], index);
        }
      }

      return { outputImages: urls, userImageUrls: this.userImageUrls };
    }
  }

  class TinypresentUploadAdapter {
    constructor(uploader) {
      this.taskId = null;
      this.uploader = uploader;
      this.userImageUrls = [];
    }

    async uploadSourceImage(file) {
      const productConfig = getProductGenerationConfig(this.uploader);
      const endpoint =
        getConfig().shopifyUploadEndpoint || "/api/shopify/upload-image";
      const signal = window.aiUploadModal?.abortController?.signal;
      const formData = new FormData();
      const uploadName =
        (file && file.name) ||
        (file && file.type === "image/png" ? "upload.png" : "upload.jpg");

      formData.append("userImage", file, uploadName);
      formData.append("customerId", getCustomerId());
      if (productConfig.shopDomain) {
        formData.append("shopDomain", productConfig.shopDomain);
      }

      const response = await fetch(getApiBaseUrl() + endpoint, {
        method: "POST",
        headers: getApiHeaders(),
        body: formData,
        signal,
      });

      const data = await parseJsonResponse(response);
      if (!response.ok || data.ok === false) {
        console.error(
          "[AiUpload] API error",
          response.status,
          endpoint,
          data
        );
        throwApiError(response, data, "User image upload failed");
      }

      const url = data.userImageUrl || data.sourceImageUrl || "";
      if (!url) {
        throw new Error("Server did not return user image URL");
      }

      return url;
    }

    async upload(file, options = {}) {
      const { secondFile, coupleMode } = options;
      const cfg = getConfig();
      const endpoint = cfg.uploadEndpoint || "/api/generate";
      const signal = window.aiUploadModal?.abortController?.signal;
      const productConfig = getProductGenerationConfig(this.uploader);

      const formData = new FormData();
      formData.append("customerId", getCustomerId());

      if (coupleMode && secondFile) {
        const [url1, url2] = await Promise.all([
          this.uploadSourceImage(file),
          this.uploadSourceImage(secondFile),
        ]);
        this.userImageUrls = [url1, url2];
        formData.append("userImageUrl", url1);
        formData.append("userImageUrl2", url2);
        formData.append("coupleMode", "true");
      } else {
        const uploadName =
          (file && file.name) ||
          (file && file.type === "image/png" ? "upload.png" : "upload.jpg");
        formData.append("userImage", file, uploadName);
      }

      if (productConfig.shopDomain) {
        formData.append("shopDomain", productConfig.shopDomain);
      }
      if (productConfig.productId) {
        formData.append("productId", String(productConfig.productId));
      }
      if (productConfig.styleDescription) {
        formData.append("styleDescription", productConfig.styleDescription);
      }
      const referenceImageUrls = Array.isArray(productConfig.referenceImageUrls)
        ? productConfig.referenceImageUrls.filter(Boolean)
        : [];
      if (referenceImageUrls.length) {
        formData.append("referenceImageUrls", JSON.stringify(referenceImageUrls));
      }

      const uploadScope =
        this.uploader?.trigger?.closest?.(".ai-upload-section") ||
        document.querySelector(".ai-upload-section");
      const generationContext =
        typeof window.getAiGenerationContext === "function"
          ? window.getAiGenerationContext(uploadScope)
          : null;

      if (generationContext?.variantId) {
        formData.append("variantId", String(generationContext.variantId));
      }
      if (generationContext?.variantTitle) {
        formData.append("variantTitle", generationContext.variantTitle);
      }
      if (
        Array.isArray(generationContext?.variantOptions) &&
        generationContext.variantOptions.length
      ) {
        formData.append(
          "variantOptions",
          JSON.stringify(generationContext.variantOptions)
        );
      }
      if (
        generationContext?.customProperties &&
        Object.keys(generationContext.customProperties).length
      ) {
        formData.append(
          "customProperties",
          JSON.stringify(generationContext.customProperties)
        );
      }

      const response = await fetch(getApiBaseUrl() + endpoint, {
        method: "POST",
        headers: getApiHeaders(),
        body: formData,
        signal,
      });

      const data = await parseJsonResponse(response);

      if (!response.ok || data.ok === false) {
        console.error(
          "[AiUpload] API error",
          response.status,
          endpoint,
          data
        );
        throwApiError(response, data, "Upload failed");
      }

      this.taskId = data.taskId;
      if (!this.taskId) {
        throw new Error("Server did not return taskId");
      }

      const uploadUrls = extractUserImageUrls(data);
      if (uploadUrls.length) {
        this.userImageUrls = uploadUrls;
      }

      return this.taskId;
    }

    async poll(uploader) {
      const cfg = getConfig();
      const pollEndpoint = cfg.pollEndpoint || "/api/task-status";
      const pollInterval =
        cfg.pollInterval || uploader?.config?.pollInterval || 2000;
      const maxAttempts =
        cfg.maxPollAttempts || uploader?.config?.maxPollAttempts || 60;
      const jobNum = cfg.jobNum || 1;

      let attempts = 0;
      let initialized = false;

      while (attempts < maxAttempts) {
        attempts++;

        if (window.aiUploadModal?.abortController?.signal.aborted) {
          throw new DOMException("Upload cancelled", "AbortError");
        }

        const pollUrl =
          getApiBaseUrl() +
          pollEndpoint +
          "?taskId=" +
          encodeURIComponent(this.taskId);

        const response = await fetch(pollUrl, {
          method: "GET",
          headers: getApiHeaders(),
          signal: window.aiUploadModal?.abortController?.signal,
        });

        const data = await parseJsonResponse(response);

        if (!response.ok || data.ok === false) {
          throwApiError(response, data, "Failed to check task status");
        }

        const status = String(data.status || "").toLowerCase();
        const outputImages = Array.isArray(data.outputImages)
          ? data.outputImages.filter(Boolean)
          : [];
        const pollUserUrls = extractUserImageUrls(data);
        if (pollUserUrls.length) {
          this.userImageUrls = pollUserUrls;
        }

        if (status === "success" && outputImages.length > 0) {
          if (!initialized) {
            window.aiUploadModal.initImageSelection();
            initialized = true;
          }
          outputImages.slice(0, jobNum).forEach((url, index) => {
            window.aiUploadModal.updateImageSlot(index, url);
            if (typeof uploader?.config?.onImageGenerated === "function") {
              uploader.config.onImageGenerated(url, index);
            }
          });
          return {
            outputImages,
            userImageUrls: this.userImageUrls,
          };
        }

        if (status === "failed") {
          window.aiUploadModal.showError(
            "",
            "Please try again with a different photo."
          );
          const err = new Error("Image generation failed");
          err.isAllFailed = true;
          throw err;
        }

        await sleep(pollInterval);
      }

      throw new Error("Processing timeout - please try again");
    }
  }

  window.getAiUploadApiAdapter = function (uploader) {
    const mode = getConfig().mode || "mock";
    if (mode === "mock") {
      return new MockUploadAdapter();
    }
    return new TinypresentUploadAdapter(uploader);
  };
})();
